import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Loader2,
  TableOfContents,
  Database,
  CheckCircle2,
  AlertTriangle,
  X,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { Pagination } from '../../InternalScan/InternalScanDashboard/Pagination';
import MultiSelectApiDropdownWithAll from '../../../components/MultiSelectApiDropdownWithAll';

interface IndexRow {
  database: string;
  schema: string;
  table: string;
  index: string;
  idx_scans: number;
  idx_tup_read: number;
  idx_tup_fetch: number;
  idx_blks_read: number;
  idx_blks_hit: number;
  last_seen: string;
  cache_hit_pct: number | null;
  rows_per_scan: number | null;
  unused: boolean;
}

interface IndexesViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export const IndexesView: React.FC<IndexesViewProps> = ({
  activeCluster,
  clientId,
  refreshKey,
}) => {
  const [loading, setLoading] = useState(true);
  const [indexData, setIndexData] = useState<IndexRow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'used' | 'unused'>('all');
  const [selectedDatabases, setSelectedDatabases] = useState<any[]>([{ id: '*', name: 'Select all' }]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchIndexTable = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const skip = (currentPage - 1) * itemsPerPage;
    const limit = itemsPerPage;

    const params: any = {
      cluster_id: activeCluster,
      skip,
      limit,
    };
    if (clientId) params.client_id = clientId;

    // API-level query filter for unused / active status
    const queryObj: Record<string, any> = {};
    if (statusFilter === 'unused') {
      queryObj.unused = 'true';
    } else if (statusFilter === 'used') {
      queryObj.unused = 'false';
    }

    // Database filtering
    const dbNames = selectedDatabases
      .map((item) => (typeof item === 'string' ? item : item?.id || item?.name || item?.database))
      .filter((name) => name && name !== '*');

    if (dbNames.length > 0) {
      params.database = dbNames.join(',');
    }

    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/indexes/table', { params });
      const rawData = res.data?.data || [];
      const total =
        res.data?.total ??
        res.data?.count ??
        (rawData.length < limit && currentPage === 1
          ? rawData.length
          : res.data?.total_count ??
            currentPage * itemsPerPage + (rawData.length === limit ? 1 : 0));

      setIndexData(Array.isArray(rawData) ? rawData : []);
      if (res.data?.total !== undefined) {
        setTotalItems(res.data.total);
      } else if (res.data?.count !== undefined) {
        setTotalItems(res.data.count);
      } else {
        setTotalItems(total);
      }
    } catch (err) {
      console.warn('IndexesView fetch error:', err);
      setIndexData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId, currentPage, itemsPerPage, statusFilter, debouncedSearch, selectedDatabases]);

  useEffect(() => {
    fetchIndexTable();
  }, [fetchIndexTable, refreshKey]);

  const handleStatusFilterChange = (newStatus: 'all' | 'used' | 'unused') => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleItemsPerPageChange = (n: number) => {
    setItemsPerPage(n);
    setCurrentPage(1);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-3.5 relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded bg-orange-500 text-white shadow-md">
            <TableOfContents className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white tracking-wide">
              Indexes Diagnostics & Performance
            </h2>
          </div>
        </div>

        {/* Filter Controls & Search */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Database Multi-Select Dropdown */}
          <div className="w-full sm:w-60">
            <MultiSelectApiDropdownWithAll
              placeholder="Select Databases"
              apiEndpoint="/db_monitor_dashboard/databases"
              additionalParams={{
                cluster_id: activeCluster,
                ...(clientId ? { client_id: clientId } : {}),
              }}
              displayProperty="name"
              idProperty="id"
              searchFields={['name']}
              value={selectedDatabases}
              onChange={(items) => {
                setSelectedDatabases(items);
                setCurrentPage(1);
              }}
              selectAll={true}
              buttonHeight={40}
            />
          </div>

          {/* Search Box */}
          <div className="relative w-full sm:w-56">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search index, table..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-900/90 border border-gray-700 rounded-lg pl-8 pr-7 py-1 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 h-8"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Status Filter Buttons (API-level query) */}
          <div className="flex items-center bg-gray-900/90 border border-gray-700 rounded-lg p-0.5 h-8">
            <button
              onClick={() => handleStatusFilterChange('all')}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                statusFilter === 'all'
                  ? 'bg-orange-500 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => handleStatusFilterChange('used')}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                statusFilter === 'used'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => handleStatusFilterChange('unused')}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
                statusFilter === 'unused'
                  ? 'bg-amber-600 text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              Unused
            </button>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl overflow-hidden shadow-xl flex flex-col relative">
        {loading && (
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-20 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        )}

        {/* Scrollable Table Area */}
        <div className="overflow-x-auto overflow-y-auto custom-scroll max-h-[calc(100vh-250px)]">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-700/80 bg-gray-900 shadow-sm text-[10px] text-gray-400 uppercase tracking-wider font-semibold whitespace-nowrap">
                <th className="py-2 px-3 bg-gray-900">Database / Schema</th>
                <th className="py-2 px-3 bg-gray-900">Table</th>
                <th className="py-2 px-3 bg-gray-900">Index Name</th>
                <th className="py-2 px-3 bg-gray-900 text-right">Scans</th>
                <th className="py-2 px-3 bg-gray-900 text-right">Tuples Read</th>
                <th className="py-2 px-3 bg-gray-900 text-right">Tuples Fetch</th>
                <th className="py-2 px-3 bg-gray-900 text-right">Blocks Hit / Read</th>
                <th className="py-2 px-3 bg-gray-900 text-right">Cache Hit %</th>
                <th className="py-2 px-3 bg-gray-900 text-right">Rows / Scan</th>
                <th className="py-2 px-3 bg-gray-900 text-center">Status</th>
                <th className="py-2 px-3 bg-gray-900 whitespace-nowrap">Last Seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30 text-gray-200 bg-gray-900/30">
              {indexData && indexData.length > 0 ? (
                indexData.map((row, idx) => {
                  const cachePct = row.cache_hit_pct;
                  const rowsPerScan = row.rows_per_scan;

                  return (
                    <tr key={`${row.database}-${row.table}-${row.index}-${idx}`} className="hover:bg-gray-800/50 transition-colors">
                      {/* Database / Schema */}
                      <td className="py-2 px-3 font-mono text-[11px] whitespace-nowrap">
                        <div className="flex items-center gap-1.5 text-gray-300">
                          <Database className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                          <span>{row.database || '-'}</span>
                          <span className="text-gray-500">/</span>
                          <span className="text-gray-400">{row.schema || 'public'}</span>
                        </div>
                      </td>

                      {/* Table */}
                      <td className="py-2 px-3 font-mono text-[11px] text-gray-300 whitespace-nowrap">
                        {row.table || '-'}
                      </td>

                      {/* Index Name */}
                      <td className="py-2 px-3 font-mono text-[11px] text-blue-300 font-semibold max-w-[200px] truncate" title={row.index}>
                        {row.index || '-'}
                      </td>

                      {/* Scans */}
                      <td className="py-2 px-3 text-right font-mono text-[11px] text-gray-200">
                        {(row.idx_scans || 0).toLocaleString()}
                      </td>

                      {/* Tuples Read */}
                      <td className="py-2 px-3 text-right font-mono text-[11px] text-gray-300">
                        {(row.idx_tup_read || 0).toLocaleString()}
                      </td>

                      {/* Tuples Fetch */}
                      <td className="py-2 px-3 text-right font-mono text-[11px] text-gray-300">
                        {(row.idx_tup_fetch || 0).toLocaleString()}
                      </td>

                      {/* Blocks Hit / Read */}
                      <td className="py-2 px-3 text-right font-mono text-[11px] text-gray-300 whitespace-nowrap">
                        <span className="text-emerald-400">{(row.idx_blks_hit || 0).toLocaleString()}</span>
                        <span className="text-gray-600 mx-1">/</span>
                        <span className="text-amber-400">{(row.idx_blks_read || 0).toLocaleString()}</span>
                      </td>

                      {/* Cache Hit % */}
                      <td className="py-2 px-3 text-right font-mono text-[11px]">
                        {cachePct !== null && cachePct !== undefined ? (
                          <span
                            className={
                              cachePct >= 95
                                ? 'text-emerald-400 font-semibold'
                                : cachePct >= 80
                                ? 'text-amber-400'
                                : 'text-rose-400'
                            }
                          >
                            {typeof cachePct === 'number' ? `${cachePct.toFixed(1)}%` : `${cachePct}%`}
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>

                      {/* Rows Per Scan */}
                      <td className="py-2 px-3 text-right font-mono text-[11px] text-gray-300">
                        {rowsPerScan !== null && rowsPerScan !== undefined ? (
                          typeof rowsPerScan === 'number' ? rowsPerScan.toFixed(1) : rowsPerScan
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>

                      {/* Status / Unused */}
                      <td className="py-2 px-3 text-center whitespace-nowrap">
                        {row.unused ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-500/10 text-amber-400 border border-amber-500/30">
                            <AlertTriangle className="w-3 h-3 text-amber-400 shrink-0" />
                            Unused
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                            <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
                            Active
                          </span>
                        )}
                      </td>

                      {/* Last Seen */}
                      <td className="py-2 px-3 font-mono text-[10px] text-gray-400 whitespace-nowrap">
                        {formatDate(row.last_seen)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} className="py-12 text-center text-gray-400 italic">
                    {searchQuery
                      ? `No indexes matching "${searchQuery}".`
                      : 'No index diagnostics telemetry available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[10, 25, 50, 100]}
        />
      </div>
    </div>
  );
};

export default IndexesView;
